import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useWebsiteContent } from "@/contexts/WebsiteContentContext";
import { usePreview } from "@/contexts/PreviewContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "react-i18next";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Contact = () => {
  const { toast } = useToast();
  const { content, isLoading } = useWebsiteContent();
  const { previewContent, isPreviewMode } = usePreview();
  const { language } = useLanguage();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = (language.startsWith('en') ? 'en' : 'ar') as 'en' | 'ar';

  // Use preview content when in preview mode, otherwise use regular content
  const activeContent = (isPreviewMode && previewContent) ? previewContent : content;

  // All hooks must be called before any early returns
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  // Don't render if content is still loading
  if (isLoading) {
    return (
      <section id="contact" className="py-24 bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </section>
    );
  }

  const contact = activeContent?.contact;

  // Simplified safety check
  if (!contact) {
    return (
      <section id="contact" className="py-24 bg-gray-50">
        <div className="container px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground">Contact content not available</p>
        </div>
      </section>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: t("contact.missingInfo"),
        description: t("contact.fillAllFields"),
        variant: "destructive",
      });
      return;
    }

    toast({
      title: t("contact.messageSent"),
      description: t("contact.responseTime"),
    });

    // Reset form
    setFormData({ name: "", email: "", message: "" });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            {contact.title?.[lang] || 'Get in Touch'}
          </h2>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            {contact.subtitle?.[lang] || "Let's discuss your publishing needs"}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="p-8 border border-primary/20 bg-white shadow-md hover:shadow-xl hover:border-primary/40 transition-all duration-500 rounded-2xl" dir={isRTL ? 'rtl' : 'ltr'}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className={`block text-sm font-medium text-foreground mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {t("contact.name")}
                </label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t("contact.namePlaceholder")}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full ${isRTL ? 'text-right' : ''}`}
                />
              </div>

              <div>
                <label htmlFor="email" className={`block text-sm font-medium text-foreground mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {t("contact.email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("contact.emailPlaceholder")}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full ${isRTL ? 'text-right' : ''}`}
                />
              </div>

              <div>
                <label htmlFor="message" className={`block text-sm font-medium text-foreground mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {t("contact.message")}
                </label>
                <Textarea
                  id="message"
                  placeholder={t("contact.messagePlaceholder")}
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className={`w-full resize-none ${isRTL ? 'text-right' : ''}`}
                />
              </div>

              <Button type="submit" variant="hero" size="lg" className="w-full rounded-full">
                {t("contact.send")}
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="p-6 border border-primary/20 bg-white shadow-md hover:shadow-xl hover:scale-105 hover:border-primary/40 transition-all duration-500 rounded-2xl group" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/15 border border-primary/30 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className={`font-semibold text-foreground mb-1 ${isRTL ? 'text-right' : ''}`}>{t("contact.emailUs")}</h3>
                  <p className={`text-foreground/70 text-sm ${isRTL ? 'text-right' : ''}`}>{contact.email || 'contact@abvi.com'}</p>
                  <p className={`text-xs text-foreground/50 mt-1 ${isRTL ? 'text-right' : ''}`}>
                    {t("contact.emailDescription")}
                  </p>
                </div>
              </div>
            </Card>

            <a
              href="https://wa.me/+971502467088"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="p-6 border border-primary/20 bg-white shadow-md hover:shadow-xl hover:scale-105 hover:border-primary/40 transition-all duration-500 rounded-2xl group cursor-pointer" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/15 border border-primary/30 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                    <WhatsAppIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className={`font-semibold text-foreground mb-1 ${isRTL ? 'text-right' : ''}`}>{t("contact.whatsapp")}</h3>
                    <p className={`text-foreground/70 text-sm ${isRTL ? 'text-right' : ''}`} dir="ltr">+971 50 246 7088</p>
                    <p className={`text-xs text-foreground/50 mt-1 ${isRTL ? 'text-right' : ''}`}>
                      {t("contact.whatsappDescription")}
                    </p>
                  </div>
                </div>
              </Card>
            </a>

            <Card className="p-6 border border-primary/20 bg-white shadow-md hover:shadow-xl hover:scale-105 hover:border-primary/40 transition-all duration-500 rounded-2xl group" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/15 border border-primary/30 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className={`font-semibold text-foreground mb-1 ${isRTL ? 'text-right' : ''}`}>{t("contact.visitUs")}</h3>
                  <p className={`text-foreground/70 text-sm ${isRTL ? 'text-right' : ''}`}>{contact.address?.[lang] || 'Riyadh, Saudi Arabia'}</p>
                  <p className={`text-xs text-foreground/50 mt-1 ${isRTL ? 'text-right' : ''}`}>
                    {t("contact.visitDescription")}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
